import {
  ScmIntegrationsApi,
  scmIntegrationsApiRef,
  ScmAuth,
  scmAuthApiRef,
} from '@backstage/integration-react';
import {
  analyticsApiRef,
  configApiRef,
  errorApiRef,
  discoveryApiRef,
  fetchApiRef,
  githubAuthApiRef,
  identityApiRef,
  microsoftAuthApiRef,
} from '@backstage/core-plugin-api';
import { scaffolderApiRef } from '@backstage/plugin-scaffolder-react';
import { ScaffolderClient } from '@backstage/plugin-scaffolder-common';
import { ApiBlueprint } from '@backstage/frontend-plugin-api';
import { MultipleAnalyticsApi } from '@backstage/core-app-api';
import { GoogleAnalytics4 } from '@backstage-community/plugin-analytics-module-ga4';
import {
  AdoptionAnalyticsCaptureApi,
  AdoptionAnalyticsClient,
} from '@codeverse-gp/plugin-adoption-analytics';

export const scmIntegrationsApiFactory = ApiBlueprint.make({
  name: 'scm-integrations',
  params: defineParams =>
    defineParams({
      api: scmIntegrationsApiRef,
      deps: { configApi: configApiRef },
      factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
    }),
});

// https://backstage.io/docs/auth/#scaffolder-configuration-software-templates
export const scmAuthApiFactory = ApiBlueprint.make({
  name: 'scm-auth',
  params: defineParams =>
    defineParams({
      api: scmAuthApiRef,
      deps: {
        azure: microsoftAuthApiRef,
        github: githubAuthApiRef,
      },

      factory: ({ azure, github }) =>
        ScmAuth.merge(
          ScmAuth.forAzure(azure),
          ScmAuth.forAuthApi(github, {
            host: 'github.com',
            scopeMapping: {
              default: [
                'repo',
                'read:org',
                'read:user',
                'read:packages',
                'workflow',
              ],
              repoWrite: [],
            },
          }),
        ),
    }),
});

// Reference: https://github.com/backstage/backstage/issues/30138#issuecomment-2943478872
export const scaffolderEventStreamApiFactory = ApiBlueprint.make({
  name: 'scaffolder-event-stream',
  params: defineParams =>
    defineParams({
      api: scaffolderApiRef,
      deps: {
        identityApi: identityApiRef,
        discoveryApi: discoveryApiRef,
        fetchApi: fetchApiRef,
        scmIntegrationsApi: scmIntegrationsApiRef,
      },
      factory: ({ identityApi, discoveryApi, fetchApi, scmIntegrationsApi }) =>
        new ScaffolderClient({
          useLongPollingLogs: true,
          identityApi,
          discoveryApi,
          fetchApi,
          scmIntegrationsApi,
        }),
    }),
});

export const analyticsApi = ApiBlueprint.make({
  name: 'analyticsApi',
  params: defineParams =>
    defineParams({
      api: analyticsApiRef,
      deps: {
        configApi: configApiRef,
        identityApi: identityApiRef,
        discoveryApi: discoveryApiRef,
        fetchApi: fetchApiRef,
        errorApi: errorApiRef,
      },
      factory: ({ configApi, identityApi, discoveryApi, fetchApi, errorApi }) =>
        MultipleAnalyticsApi.fromApis([
          GoogleAnalytics4.fromConfig(configApi, { identityApi }),
          new AdoptionAnalyticsCaptureApi({
            adoptionAnalyticsApi: new AdoptionAnalyticsClient({
              discoveryApi,
              fetchApi,
            }),
            onError: (error: unknown) =>
              errorApi.post(
                error instanceof Error ? error : new Error(String(error)),
                { hidden: true },
              ),
          }),
        ]),
    }),
});
